import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { StandardDialog, TabConfig } from '../../../components/dialogs/base';
import { DeleteButton } from '../../../components/buttons';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { apiGet, apiPost, apiDelete, handleApiError } from '../../../services/apiClient';
import { Driver, UpdateDriverRequest } from '../../../models/driver';
import { VehicleWithDriver } from '../../../models/vehicle';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../utils/toast';
import { useVehicleContext } from '../../../context/vehicleContext';
import { useReservations } from '../../../hooks';
import { getDriverValidationSchema } from './driverFormConstants';
import { DriverFormFields } from './DriverFormFields';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { EventSourceInput } from '@fullcalendar/core';
import { Booking } from '../../../models/booking';

type Props = {
  open: boolean;
  onClose: () => void;
  driver: Driver | null;
  readOnly?: boolean;
  onSuccess?: (message: string) => void;
};

export default function DriverSettingsModal({ open, onClose, driver, readOnly = false, onSuccess }: Props) {
  const { t } = useTranslation();
  const { vehicles: vehiclesFromContext, isLoading: vehiclesLoading, fetchVehicles } = useVehicleContext();
  const vehicles = vehiclesFromContext as VehicleWithDriver[];

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  // State for driver schedule events
  const [scheduleEvents, setScheduleEvents] = useState<EventSourceInput>([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  // Effect to fetch driver bookings for schedule
  useEffect(() => {
    if (!open || !driver) return;
    setScheduleLoading(true);
    apiGet(`${process.env.REACT_APP_API_URL}/api/v1/driver/${driver.id}/bookings`)
      .then(response => response.json())
      .then(payload => {
        if (!payload?.result) {
          setScheduleEvents([]);
          return;
        }
        const events = payload.result.map((booking: Booking) => {
          const loadingPlaces = booking.loadingPlaces;
          const fromCity = loadingPlaces.filter((loadingPlace) => loadingPlace.loadingType === 'LOADING').sort((a, b) => a.startTime.localeCompare(b.startTime))[0]?.address.city || '';
          const toCity = loadingPlaces.filter((loadingPlace) => loadingPlace.loadingType === 'UNLOADING').sort((a, b) => b.startTime.localeCompare(a.startTime))[0]?.address.city || '';
          return {
            title: `${fromCity} -> ${toCity}`,
            start: booking.startTime,
            end: booking.endTime
          };
        });
        setScheduleEvents(events);
      })
      .catch(() => setScheduleEvents([]))
      .finally(() => setScheduleLoading(false));
  }, [open, driver]);

  // Use lockedDriver to lock driver selection in reservation modal.
  const {
    Dialog: ReservationDialog,
    setVehicleId,
    setFormData: setReservationFormData,
    setIsOpen: setReservationDialogOpen
  } = useReservations(driver ? { lockedDriver: driver, onSuccess: () => fetchVehicles({ with_drivers: true }).catch(err => { handleApiError(err, t('Failed to fetch vehicles!')); }) } : undefined);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<UpdateDriverRequest & { coordinates: string }>({
    resolver: yupResolver(getDriverValidationSchema(t)) as any,
  });

  // Effect to populate form with driver data when modal opens
  useEffect(() => {
    if (open) {
      setError(null);
      // Only fetch vehicles if not already loaded or if we need driver assignment info
      fetchVehicles({ with_drivers: true }).catch(err => {
        handleApiError(err, t('Failed to fetch vehicles!'));
      });

    }

    if (driver && open) {
      const coordinates = driver.mainLocation
        ? `${driver.mainLocation.latitude}, ${driver.mainLocation.longitude}`
        : '';

      reset({
        first_name: driver.firstName || '',
        last_name: driver.lastName || '',
        email: driver.email || '',
        coordinates: coordinates,
        known_languages: driver.knownLanguages || [],
        safety_certificates: driver.safetyCertificates || [],
        base_country: driver.baseCountry || '',
      });
    }
  }, [driver, open, reset, fetchVehicles, vehicles.length, t]);

  const onSubmit = async (data: UpdateDriverRequest & { coordinates: string }) => {
    if (!driver || readOnly) return;

    setLoading(true);
    setError(null);

    try {
      // Parse coordinates string into latitude and longitude
      const coordinateParts = data.coordinates.split(',').map(part => part.trim());
      const latitude = parseFloat(coordinateParts[0]);
      const longitude = parseFloat(coordinateParts[1]);

      // Create API payload with separated coordinates
      const apiPayload: UpdateDriverRequest = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        main_location: {
          latitude: latitude,
          longitude: longitude
        },
        known_languages: data.known_languages,
        safety_certificates: data.safety_certificates,
        base_country: data.base_country
      };
      await apiPost(`${process.env.REACT_APP_API_URL}/api/v1/driver/${driver.id}`, apiPayload);
      if (onSuccess) {
        onSuccess(t('Driver updated successfully!'));
      }
      onClose();
    } catch (_) {
      toast.error(t('Failed to update driver'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDriver = async () => {
    if (!driver) return;

    setLoading(true);
    setError(null);

    try {
      await apiDelete(`${process.env.REACT_APP_API_URL}/api/v1/driver/${driver.id}`);

      if (onSuccess) {
        onSuccess(t('Driver deleted successfully!'));
      }
      onClose();
    } catch (_) {
      setError(t('Failed to delete driver'));
      toast.error(t('Failed to delete driver'));
    } finally {
      setLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  // Filter vehicles based on search term
  const filteredVehicles = useMemo(() => {
    if (!searchTerm) return vehicles;
    const searchLower = searchTerm.toLowerCase();
    return vehicles.filter(vehicle => vehicle.truckPlate?.toLowerCase().includes(searchLower) ||
      vehicle.trailerPlate?.toLowerCase().includes(searchLower) ||
      vehicle.name?.toLowerCase().includes(searchLower) ||
      vehicle.manufacturer?.toLowerCase().includes(searchLower) ||
      vehicle.model?.toLowerCase().includes(searchLower)
    );
  }, [vehicles, searchTerm]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = vehicles.length;
    const assigned = vehicles.filter(vehicle => vehicle.driverId).length;
    const available = total - assigned;
    return { total, assigned, available };
  }, [vehicles]);

  const { total: totalVehicles, assigned: assignedVehicles, available: availableVehicles } = stats;

  const handleAssignVehicle = (vehicle: VehicleWithDriver) => {
    setVehicleId(vehicle.id);
    setReservationFormData({
      vehicleId: vehicle.id,
      vehicle,
      driver: driver ?? undefined,
    });
    setReservationDialogOpen(true);
    // refresh vehicles after assignment to update driver assignment status
    fetchVehicles({ with_drivers: true }).catch(err => {
      handleApiError(err, t('Failed to fetch vehicles!'));
    });
  };


  const renderPersonalInformation = () => (
    <Box sx={styles.formContainer}>
      <Box sx={styles.formGrid}>
        <DriverFormFields
          control={control}
          errors={errors}
          onSubmit={handleSubmit(onSubmit)}
          readOnly={readOnly}
        />
      </Box>
    </Box>
  );

  const renderReservations = () => (
    <Box sx={{ pr: { xs: -1, sm: -1.5 }, mr: { xs: -1, sm: -3.5, md: -3 }, py: { xs: 2, sm: 2.5, md: 3 } }}>
      {/* Header with Search */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 0,
        mb: 2
      }}>
        <Typography variant="h6" sx={{ color: 'text.dark', fontWeight: 600, paddingLeft: { xs: 2, sm: 2.5, md: 3 } }}>
          {t('Available Vehicles')}
        </Typography>
        <TextField
          placeholder={t('Search vehicles...')}
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            width: 300,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'text.black'
              }
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'text.black'
            }
          }}
        />
      </Box>

      {/* Quick Stats */}
      <Box sx={{
        display: 'flex',
        gap: 2,
        mb: 2,
        padding: { xs: 2, sm: 2.5, md: 3 },
        pt: 0
      }}>
        {[
          { label: t('Total Vehicles'), value: totalVehicles },
          { label: t('Available Vehicles'), value: availableVehicles },
          { label: t('Assigned Vehicles'), value: assignedVehicles }
        ].map((stat, index) => (
          <Box
            key={index}
            sx={{
              flex: 1,
              p: 2,
              backgroundColor: 'background.grey',
              borderRadius: 2,
              border: (theme: any) => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.border.purpleLight : theme.palette.border.light}`
            }}
          >
            <Typography sx={{ fontSize: (theme: any) => theme.typography.fontSizes.small, color: 'text.grey', mb: 0.5 }}>
              {stat.label}
            </Typography>
            <Typography sx={{ fontSize: (theme: any) => theme.typography.fontSizes.heading2, fontWeight: 600, color: 'text.dark' }}>
              {stat.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Vehicles Table */}
      <Box sx={{
        border: '1px solid border.divider',
        borderLeft: 'none',
        borderRight: 'none',
        borderRadius: 0,
        overflow: 'hidden',
        overflowX: 'hidden',
        backgroundColor: 'transparent',
        width: '100%',
        maxWidth: '100%'
      }}>
        {/* Table Header */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(60px, 0.6fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.3fr) minmax(0, 1fr) minmax(90px, 0.8fr)',
          gap: 1.5,
          p: 2,
          backgroundColor: 'transparent',
          borderBottom: '1px solid border.divider',
          fontWeight: 600,
          color: 'text.darkGrey',
          fontSize: (theme: any) => theme.typography.fontSizes.body,
          minWidth: 0,
          width: '100%'
        }}>
          <Box sx={styles.tableHeaderCell}>{t('ID')}</Box>
          <Box sx={styles.tableHeaderCell}>{t('Truck Plate')}</Box>
          <Box sx={styles.tableHeaderCell}>{t('Trailer Plate')}</Box>
          <Box sx={styles.tableHeaderCell}>{t('Truck Type')}</Box>
          <Box sx={styles.tableHeaderCell}>{t('Assigned Driver')}</Box>
          <Box sx={styles.tableHeaderCell}>{t('Actions')}</Box>
        </Box>

        {/* Table Rows */}
        <Box sx={{ width: '100%' }}>
          {vehiclesLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography sx={{ color: 'text.grey' }}>{t('Loading trucks...')}</Typography>
            </Box>
          ) : filteredVehicles.length > 0 ? (
            filteredVehicles.map((vehicle) => (
              <Box
                key={vehicle.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(60px, 0.6fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.3fr) minmax(0, 1fr) minmax(90px, 0.8fr)',
                  gap: 1.5,
                  p: 2,
                  borderBottom: '1px solid border.light',
                  alignItems: 'center',
                  minWidth: 0,
                  width: '100%',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: (theme: any) => theme.palette.mode === 'light' ? 'background.purpleLight' : 'background.darkGrey'
                  }
                }}
              >
                <Typography sx={styles.tableCellMuted}>
                  {vehicle.id}
                </Typography>
                <Typography sx={styles.tableCell}>
                  {vehicle.truckPlate || 'N/A'}
                </Typography>
                <Typography sx={styles.tableCell}>
                  {vehicle.trailerPlate || 'N/A'}
                </Typography>
                <Typography sx={styles.tableCell}>
                  {vehicle.manufacturer && vehicle.model ? `${vehicle.manufacturer} ${vehicle.model}` : vehicle.name || 'N/A'}
                </Typography>
                <Typography sx={{ ...styles.tableCell, color: vehicle.driverId ? 'text.dark' : 'text.grey' }}>
                  {vehicle.driverId ? t('Assigned') : t('Not assigned')}
                </Typography>
                <Button
                  variant={vehicle.driverId ? 'outlined' : 'contained'}
                  size="small"
                  disabled={!!vehicle.driverId || readOnly}
                  onClick={() => !vehicle.driverId && handleAssignVehicle(vehicle)}
                  sx={{
                    backgroundColor: vehicle.driverId ? 'transparent' : (theme: any) => theme.palette.mode === 'light' ? 'text.purple' : 'background.shadowBlack',
                    color: vehicle.driverId ? 'text.light' : 'text.white',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: (theme: any) => theme.typography.fontSizes.small,
                    borderColor: vehicle.driverId ? 'border.medium' : 'transparent',
                    minWidth: 'fit-content',
                    '&:hover': {
                      backgroundColor: vehicle.driverId ? 'transparent' : (theme: any) => theme.palette.mode === 'light' ? 'background.purpleHover' : 'background.veryDarkGrey'
                    }
                  }}
                >
                  {vehicle.driverId ? t('Assigned') : t('Assign')}
                </Button>
              </Box>
            ))
          ) : (
            <Box sx={{
              p: 4,
              textAlign: 'center',
              color: 'text.grey'
            }}>
              <Typography sx={{ fontSize: (theme: any) => theme.typography.fontSizes.body, mb: 1 }}>
                {t('No vehicles found')}
              </Typography>
              <Typography sx={{ fontSize: (theme: any) => theme.typography.fontSizes.small }}>
                {searchTerm ? t('Try adjusting your search criteria') : t('No vehicles available')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

    </Box>
  );

  const renderDocuments = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 3, color: 'text.dark', fontWeight: 600 }}>
        {t('Driver Documents')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
          p: { xs: 3, sm: 4, md: 5 },
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1,
            '& img': {
              width: { xs: '200px', sm: '250px', md: '300px' },
              height: 'auto',
            },
          }}
        >
          <img src="/Market launch-pana (1).svg" alt="Coming soon" />
        </Box>
        <Box sx={{ mt: 3 }}>
          <Typography
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
              color: 'text.grey',
              lineHeight: 1.6,
            }}
          >
            {t("We're currently building this feature.")}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
              color: 'text.grey',
              lineHeight: 1.6,
              mt: 1,
            }}
          >
            {t('It will be available in a future update.')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const renderSchedule = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 3, color: 'text.dark', fontWeight: 600 }}>
        {t('Driver’s Schedule')}
      </Typography>
      <Box sx={{
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'stretch',
        width: '100%',
        minHeight: 200,
        border: '2px dashed border.default',
        borderRadius: 2,
        backgroundColor: 'background.white',
        '& .fc': {
          width: '100%'
        }
      }}>
        <Box sx={{ width: '100%' }}>
          {scheduleLoading ? (
            <Typography sx={{ color: 'text.grey', p: 4, textAlign: 'center' }}>{t('Loading schedule...')}</Typography>
          ) : (
            <FullCalendar
              plugins={[dayGridPlugin]}
              initialView="dayGridWeek"
              headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridDay,dayGridWeek,dayGridMonth' }}
              contentHeight="auto"
              events={scheduleEvents}
            />
          )}
        </Box>
      </Box>
    </Box>
  );


  if (!open || !driver) return null;

  const tabs: TabConfig[] = [
    {
      label: t('Personal Information'),
      content: renderPersonalInformation()
    },
    {
      label: t('Reservations'),
      content: renderReservations()
    },
    {
      label: t('Documents'),
      content: renderDocuments()
    },
    {
      label: t('Schedule'),
      content: renderSchedule()
    }
  ];

  return (
    <>
      <StandardDialog
        open={open}
        onClose={onClose}
        title={driver ? `${driver.firstName} ${driver.lastName}` : t('Unknown Driver')}
        subtitle={readOnly ? t('Driver Information (View Only)') : t('Driver Settings')}
        tabs={tabs}
        maxWidth="sm"
        width={{ xs: '100%', sm: '90%', md: '700px', lg: '700px', xl: '900px' }}
        height={{ xs: '100vh', sm: '95vh', md: '90vh', lg: '90vh', xl: '90vh' }}
        error={error}
        headerActions={
          !readOnly ? (
            <DeleteButton onClick={() => setDeleteConfirmOpen(true)} disabled={loading}>
              {t('DELETE DRIVER')}
            </DeleteButton>
          ) : undefined
        }
        primaryAction={
          !readOnly ? {
            label: loading ? t('Saving...') : t('Save Changes'),
            onClick: handleSubmit(onSubmit),
            loading: loading,
            disabled: loading
          } : undefined
        }
        secondaryAction={{
          label: readOnly ? t('Close') : t('Cancel'),
          onClick: onClose
        }}
      />

      {/* Assignment Modal */}
      {ReservationDialog}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{
              fontWeight: 600,
              color: 'text.darkGrey',
              mb: 1
            }}>
              {t('Delete Driver')} {driver?.firstName} {driver?.lastName}
            </Typography>
            <Typography variant="body2" sx={{
              color: 'text.grey',
              mb: 3
            }}>
              {t('Are you sure you want to delete this driver? This action cannot be undone.')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={() => setDeleteConfirmOpen(false)}
              variant="outlined"
              fullWidth
              sx={{
                borderRadius: 2,
                borderColor: 'border.medium',
                color: 'text.darkGrey',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'border.dark',
                  backgroundColor: 'background.lightGrey'
                }
              }}
            >
              {t('Cancel')}
            </Button>
            <Button
              onClick={handleDeleteDriver}
              variant="contained"
              color="error"
              fullWidth
              disabled={loading}
              sx={{
                borderRadius: 2,
                fontWeight: 500,
                textTransform: 'none',
                '&:disabled': {
                  backgroundColor: 'background.darkGrey'
                }
              }}
            >
              {loading ? t('Deleting...') : t('Delete')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

const styles = {
  formContainer: {
    flex: 1,
    overflow: 'auto',
    p: 2
  },
  formGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  },
  tableHeaderCell: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  tableCell: {
    fontSize: (theme: any) => theme.typography.fontSizes.body,
    fontWeight: 500,
    color: 'text.dark',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  tableCellMuted: {
    fontSize: (theme: any) => theme.typography.fontSizes.body,
    color: 'text.grey',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }
};
